import React from 'react';
import { useToast, ToastType } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';

const Icons: Record<ToastType, React.ReactNode> = {
  success: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="15" y1="9" x2="9" y2="15"></line>
      <line x1="9" y1="9" x2="15" y2="15"></line>
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
      <line x1="12" y1="9" x2="12" y2="13"></line>
      <line x1="12" y1="17" x2="12.01" y2="17"></line>
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  ),
};

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();
  const { isDark } = useTheme();

  const getToastStyles = (type: ToastType) => {
    const styles = {
      success: isDark
        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
        : 'bg-emerald-50 border-emerald-200 text-emerald-800',
      error: isDark
        ? 'bg-red-500/10 border-red-500/30 text-red-400'
        : 'bg-red-50 border-red-200 text-red-800',
      warning: isDark
        ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
        : 'bg-amber-50 border-amber-200 text-amber-800',
      info: isDark
        ? 'bg-blue-500/10 border-blue-500/30 text-blue-400'
        : 'bg-blue-50 border-blue-200 text-blue-800',
    };
    return styles[type];
  };

  const getIconStyles = (type: ToastType) => {
    const styles = {
      success: isDark ? 'text-emerald-400' : 'text-emerald-600',
      error: isDark ? 'text-red-400' : 'text-red-600',
      warning: isDark ? 'text-amber-400' : 'text-amber-600',
      info: isDark ? 'text-blue-400' : 'text-blue-600',
    };
    return styles[type];
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-md">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-sm animate-in slide-in-from-right duration-300 ${getToastStyles(toast.type)}`}
          role="alert"
        >
          <div className={`flex-shrink-0 ${getIconStyles(toast.type)}`}>
            {Icons[toast.type]}
          </div>
          <p className="flex-1 text-sm font-medium">{toast.message}</p>
          <button
            onClick={() => removeToast(toast.id)}
            className={`flex-shrink-0 p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
